import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import { PenLine, X, Loader2, Feather } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { LoginArea } from '@/components/auth/LoginArea';
import { WISDOM_KIND } from '@/hooks/useWisdomLetters';
import { useToast } from '@/hooks/useToast';

const TOPIC_OPTIONS = ['relationships', 'career', 'health', 'money', 'family', 'mindset', 'education', 'travel'];

const LETTER_PLACEHOLDER = `Dear [younger me],

I wish I had known that...

The thing nobody told me was...

If I could go back, I would tell you...

With love and hindsight,
[your name]`;

export default function WritePage() {
  useSeoMeta({
    title: 'Write a Letter — Hindsight Wisdom',
    description: 'Share what you wish you had known. Write a letter to your past self.',
  });

  const { user } = useCurrentUser();
  const { mutateAsync: publish, isPending } = useNostrPublish();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [customTopic, setCustomTopic] = useState('');

  const toggleTopic = (topic: string) => {
    setSelectedTopics((prev) =>
      prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic]
    );
  };

  const addCustomTopic = () => {
    const t = customTopic.trim().toLowerCase().replace(/\s+/g, '-');
    if (t && !selectedTopics.includes(t)) {
      setSelectedTopics((prev) => [...prev, t]);
    }
    setCustomTopic('');
  };

  const removeTopic = (topic: string) => {
    setSelectedTopics((prev) => prev.filter((t) => t !== topic));
  };

  const isValid = title.trim().length > 0 && subject.trim().length > 0 && body.trim().length > 20;
  const wordCount = body.trim().split(/\s+/).filter(Boolean).length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || !user) return;

    try {
      const tags: string[][] = [
        ['title', title.trim()],
        ['subject', subject.trim()],
        ['alt', 'A wisdom letter to a past self on Hindsight Wisdom'],
        ...selectedTopics.map((t) => ['t', t]),
      ];

      const event = await publish({
        kind: WISDOM_KIND,
        content: body.trim(),
        tags,
      });

      toast({ title: 'Letter published!', description: 'Your wisdom is now on Nostr.' });
      navigate(`/${nip19.noteEncode(event.id)}`);
    } catch {
      toast({
        title: 'Failed to publish',
        description: 'Something went wrong. Check your relay connection.',
        variant: 'destructive',
      });
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="container py-20 text-center max-w-md mx-auto">
          <Feather className="w-12 h-12 text-primary/40 mx-auto mb-5" />
          <h1 className="font-serif text-2xl font-bold mb-3">Sign in to write your letter</h1>
          <p className="text-muted-foreground mb-8 text-base leading-relaxed">
            Your letter will be published to Nostr — permanently, uncensorably, and under your own identity.
          </p>
          <LoginArea className="mx-auto w-full max-w-48" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-10 max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-bold text-foreground mb-2 flex items-center gap-3">
            <PenLine className="w-7 h-7 text-primary" />
            Write your letter
          </h1>
          <p className="text-muted-foreground">
            What do you wish you had known? Share your hindsight with the world.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-base font-medium">
              Letter title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              placeholder="e.g. What I wish I knew at 22"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              className="text-base"
            />
            <p className="text-xs text-muted-foreground">{title.length}/120</p>
          </div>

          {/* Subject / Recipient */}
          <div className="space-y-2">
            <Label htmlFor="subject" className="text-base font-medium">
              To whom? <span className="text-destructive">*</span>
            </Label>
            <Input
              id="subject"
              placeholder="e.g. 16-year-old me, me in 2008, my 20s self"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={80}
              className="text-base"
            />
          </div>

          {/* Topics */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Topics (optional)</Label>
            <div className="flex flex-wrap gap-2">
              {TOPIC_OPTIONS.map((topic) => (
                <button
                  key={topic}
                  type="button"
                  onClick={() => toggleTopic(topic)}
                  className={`px-3 py-1 rounded-full text-sm font-medium border transition-all ${
                    selectedTopics.includes(topic)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
                  }`}
                >
                  {topic}
                </button>
              ))}
            </div>

            {/* Custom topic input */}
            <div className="flex gap-2">
              <Input
                placeholder="Add custom topic…"
                value={customTopic}
                onChange={(e) => setCustomTopic(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomTopic();
                  }
                }}
                className="max-w-48"
              />
              <Button type="button" variant="outline" size="sm" onClick={addCustomTopic}>
                Add
              </Button>
            </div>

            {/* Selected custom topics */}
            {selectedTopics.filter((t) => !TOPIC_OPTIONS.includes(t)).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selectedTopics
                  .filter((t) => !TOPIC_OPTIONS.includes(t))
                  .map((topic) => (
                    <span
                      key={topic}
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent text-accent-foreground border border-border"
                    >
                      {topic}
                      <button
                        type="button"
                        onClick={() => removeTopic(topic)}
                        className="text-muted-foreground hover:text-foreground"
                        aria-label={`Remove ${topic}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
              </div>
            )}
          </div>

          {/* Body */}
          <div className="space-y-2">
            <Label htmlFor="body" className="text-base font-medium">
              Your letter <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="body"
              placeholder={LETTER_PLACEHOLDER}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              className="font-serif text-base leading-relaxed resize-y min-h-64"
            />
            <p className="text-xs text-muted-foreground">{wordCount} words</p>
          </div>

          {/* Submit */}
          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-muted-foreground max-w-xs leading-relaxed">
              This letter will be published permanently to the Nostr network. Anyone can read it.
            </p>
            <Button
              type="submit"
              size="lg"
              className="rounded-full gap-2 px-8"
              disabled={!isValid || isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Publishing…
                </>
              ) : (
                <>
                  <PenLine className="w-4 h-4" />
                  Publish letter
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
